// LinkedIn Scrape Result ***************************************************************************
// pathname: GET /v1/resume/linkedin-scrape/{scrapeId}
// ************************************************************************************************
// SWR hook to fetch the proposal generated by a LinkedIn scrape job once it has reached the `ready`
// state. Should only be enabled by the consumer after the status hook reports a terminal value.
// ************************************************************************************************
import { useSwrApi } from '@hooks/useSwrApi/useSwrApi';
import type { LinkedinScrapeResultResponse } from '@type/linkedinScrape';


interface SwrLinkedinScrapeResultProps {
  scrapeId: string | null;
  enabled: boolean;
  locale?: Locale;
}


/**
 * @description SWR: fetch the proposal payload of a finished LinkedIn scrape job.
 *
 * @param props - hook options.
 * @param props.scrapeId - the id of the LinkedIn scrape job; pass `null` to disable fetching.
 * @param props.enabled - whether the consumer wants to fetch (e.g. status is terminal & ready).
 * @param props.locale - the locale to use for the request. Default: 'ro'.
 */
export const useSwrLinkedinScrapeResult = (props: SwrLinkedinScrapeResultProps) => {
  const {
    scrapeId,
    enabled,
    locale = 'ro',
  } = props;

  const shouldFetch = Boolean(scrapeId) && enabled;

  const {
    data, error, isLoading, isValidating, mutate,
  } = useSwrApi<LinkedinScrapeResultResponse>({
    url: scrapeId ? `/v1/resume/linkedin-scrape/${encodeURIComponent(scrapeId)}` : '',
    shouldFetch,
    locale,
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // 1 minute
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  };
};
